import React, { memo } from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import SplitPaneMAIQ from 'app/NewAIAvatarLayout/sharedComponents/SplitPaneMAIQ';
import CleanChatPanel from './CleanChatPanel';
import { Canvas } from './Canvas';
import { ChatState, MiaChatInterfaceProps } from '../types';
import EmailPreviewModal from 'app/NewAIAvatarLayout/sharedComponents/EmailPreviewModal';
import { GenerationProgressPopupMAIQ } from 'app/NewAIAvatarLayout/sharedComponents/GenerationProgressPopupMAIQ';

const InterfaceContainer = styled(Box)({
  display: 'flex',
  height: '100vh',
  width: '100%',
  boxSizing: 'border-box',
  overflow: 'hidden',
});

export const MiaChatInterface: React.FC<MiaChatInterfaceProps> = memo(({
  canvasEnabled = true,
  conversationId,
  trackingId,
  chatState,
  onOptionSelect,
  onSendMessage,
  onReset,
  updateInputText,
  isChatInputDisabled = false,
  progressCards = [],
  isLoading = false,
  isGeneratingEmail = false,
  isEmailGenerationModalOpen = false,
  streamingData,
  currentSection,
  onOpenEmailGenerationPopup,
  generatedEmail,
  isEmailPreviewOpen,
  isSocketConnected = true,
  isStreamingStalled = false,
  onOpenEmailPreview,
  onCloseEmailPreview,
  onOpenEmailGenerationProgress,
  onCloseEmailGenerationProgress,
  defaultWidth = 70,
  minWidth = 30,
  maxWidth = 80,
  useSplitPane = true,
}) => {
  const chatPanel = (
    <CleanChatPanel
      chatHistory={chatState.chatHistory}
      onOptionSelect={onOptionSelect}
      onSendMessage={onSendMessage}
      inputText={chatState.inputText}
      setInputText={updateInputText}
      loading={chatState.loading || isLoading}
      onReset={onReset}
      suggestions={chatState.suggestions}
      isInputRestricted={chatState.is_input_restricted_to_suggestions || isChatInputDisabled}
    />
  );

  const canvasPanel = canvasEnabled ? (
    <Canvas
      isLoading={isLoading}
      details={progressCards}
      isGeneratingEmail={isGeneratingEmail}
      streamingData={streamingData}
      currentSection={currentSection}
      onOpenEmailGenerationPopup={onOpenEmailGenerationProgress || onOpenEmailGenerationPopup}
    />
  ) : null;

  return (
    <InterfaceContainer>
      {useSplitPane ? (
        <SplitPaneMAIQ
          left={chatPanel}
          right={canvasPanel}
          defaultWidth={defaultWidth}
          minWidth={minWidth}
          maxWidth={maxWidth}
        />
      ) : (
        chatPanel
      )}
      {/* Email Preview Modal */}
      <EmailPreviewModal
        open={isEmailPreviewOpen || false}
        onClose={onCloseEmailPreview || (() => {})}
        generatedEmail={generatedEmail}
        conversationId={conversationId || undefined}
        trackingId={trackingId ?? undefined}
      />
      
      {/* Email Generation Progress Modal */}
      <GenerationProgressPopupMAIQ
        open={isEmailGenerationModalOpen && isGeneratingEmail}
        isGenerating={isGeneratingEmail}
        streamingData={streamingData}
        currentSection={currentSection || undefined}
        sections={streamingData?.message?.sections || []}
        isFinal={streamingData?.message?.isFinal || false}
        showProgress={true}
        message="Crafting your message..."
        isSocketConnected={isSocketConnected}
        isStreamingStalled={isStreamingStalled}
        onClose={onCloseEmailGenerationProgress || (() => {})}
      />
    </InterfaceContainer>
  );
});

MiaChatInterface.displayName = 'MiaChatInterface';

